import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import API from '../api/Api';
import initialState from '../constants/initialState';

// Define initialstate
const initStates = {
    language: initialState.language,
    isFetching: initialState.isFetching,
    isSuccess: initialState.isSuccess,
    isError: initialState.isError,
    errorMessage: initialState.errorMessage,
    orderList: [],
}

//Get all orders
export const getOrders = createAsyncThunk(
    'order/list',
    async (thunkAPI) => {
        try {
            const result = await API.getData('order');
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                toast(response.message, { position: 'bottom-left' });
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

// Update an order's status (kitchen workflow), then refresh the list
export const updateOrderStatus = createAsyncThunk(
    'order/updateStatus',
    async ({ id, order_status }, thunkAPI) => {
        try {
            const result = await API.putData(`order/status/${id}`, { order_status });
            const response = result.data;
            if (response.status) {
                toast.success(i18n.t('orders.toast_status_updated'));
                thunkAPI.dispatch(getOrders());
                return response.data;
            } else {
                toast.error(response.message || i18n.t('orders.toast_status_failed'));
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            toast.error(i18n.t('orders.toast_status_failed'));
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

// Record a bill as Paid / Pending (+ settle method), then refresh the list. CometQR moves no
// money — this is the owner's accounting record (cash/transfer settled outside the app).
export const updateOrderPayment = createAsyncThunk(
    'order/updatePayment',
    async ({ id, payment_status, settle_method }, thunkAPI) => {
        try {
            const result = await API.putData(`order/payment/${id}`, { payment_status, settle_method });
            const response = result.data;
            if (response.status) {
                toast.success(payment_status === 'paid' ? i18n.t('orders.toast_marked_paid') : i18n.t('orders.toast_marked_pending'));
                thunkAPI.dispatch(getOrders());
                return response.data;
            } else {
                toast.error(response.message || i18n.t('orders.toast_payment_failed'));
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            toast.error(i18n.t('orders.toast_payment_failed'));
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

const Orders = createSlice({
    name: 'order',
    initialState: initStates,
    reducers: {
        clearState(state) {
            Object.assign(state, initStates);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getOrders.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getOrders.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.isError = false;
                state.errorMessage = '';
                state.isSuccess = true;
                state.orderList = payload ?? [];
            })
            .addCase(getOrders.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Could not load orders.';
            });
    }
});

export const {
    clearState,
} = Orders.actions;
export default Orders.reducer;